import { VStack, Text, Grid, Box, Stack, Divider } from "@chakra-ui/react";
import { useMemo } from "react";
import { EventDetailTicketSalesSection } from "@common/interfaces/KYDEvent";
import { useEventMetrics } from "../hooks/useEventMetrics";
import { TicketSalesChart } from "./TicketSalesChart";

interface Props {
  label: string;
  value: string;
  height?: string;
}

export const Stat = ({ label, value, height }: Props) => {
  return (
    <VStack
      bgGradient="linear(180deg, rgba(231, 233, 239, 0.3) 0%, #FFF 100%)"
      boxShadow="0px 1px 2px 0px rgba(255, 255, 255, 0.75) inset, 0px 4px 12px -5px rgba(0, 0, 0, 0.04)"
      position="relative"
      border={"1px solid #DDDDDD"}
      borderRadius={"10px"}
      alignItems={"flex-start"}
      p={"12px"}
      gap={"4px"}
      width={"100%"}
      height={height}
      lineHeight={"100%"}
    >
      <Text fontSize={"13px"} fontWeight={"500"} color={"#666666"}>
        {label}
      </Text>
      <Text fontSize={"20px"} fontWeight={"400"} lineHeight={"24px"}>
        {value}
      </Text>
    </VStack>
  );
};

interface StatSection {
  title: string;
  subtitle?: string;
  stats: Array<{ title: string; value: string }>;
}

const TicketSalesSection = ({
  isLoading,
  data,
}: {
  isLoading?: boolean;
  data: EventDetailTicketSalesSection["data"];
}) => {
  const { metrics, loading: isLoadingMetrics } = useEventMetrics(data.id);
  const isDraftEvent = data.status === "DRAFT";

  // Group stats into sections based on their titles
  const statSections = useMemo((): StatSection[] => {
    if (!metrics?.stats || metrics.stats.length === 0) return [];

    // Define the stat groupings
    const payoutsKeys = ["Payout Balance", "Hold Balance"];
    const attendanceKeys = ["Total", "Ticket Buyers", "Guestlist"];

    // Create a map for quick lookup
    const statsMap = new Map(metrics.stats.map((stat) => [stat.title, stat]));
    const ticketMetricsKeys = [
      "Sales",
      "Total Tickets",
      "Box Office Cash",
      "Refunds",
      "Disputes",
      "Discounts",
      "Rebates",
      "Sales + Rebates",
      "Total Venue Sales",
    ];

    const sections: StatSection[] = [];

    // Ticket Metrics section
    const ticketMetricsStats = ticketMetricsKeys
      .map((key) => statsMap.get(key))
      .filter(Boolean) as Array<{ title: string; value: string }>;
    if (ticketMetricsStats.length > 0) {
      sections.push({
        title: "Ticket Metrics",
        stats: ticketMetricsStats,
      });
    }

    // Payouts section
    const payoutsStats = payoutsKeys
      .map((key) => statsMap.get(key))
      .filter(Boolean) as Array<{ title: string; value: string }>;
    if (payoutsStats.length > 0) {
      sections.push({
        title: "Payouts",
        stats: payoutsStats,
      });
    }

    // Attendance section
    const attendanceStats = attendanceKeys
      .map((key) => statsMap.get(key))
      .filter(Boolean) as Array<{ title: string; value: string }>;
    if (attendanceStats.length > 0) {
      sections.push({
        title: "Attendance",
        subtitle: "People who checked in",
        stats: attendanceStats,
      });
    }

    return sections;
  }, [metrics?.stats]);

  return (
    <>
      {!isDraftEvent ? (
        <VStack w="100%" spacing={4} alignItems="stretch">
          {statSections.map((section) => (
            <Stack key={section.title}>
              <Stack
                direction={{ base: "column", md: "row" }}
                alignItems={"top"}
              >
                <Box
                  w={{ base: "100%", md: "20%" }}
                  display={{ base: "block", md: "inline-block" }}
                >
                  <Text
                    fontSize={"16px"}
                    fontWeight={"600"}
                    color={"#000"}
                    mb={{ base: 1, md: section.subtitle ? "4px" : "16px" }}
                  >
                    {section.title}
                  </Text>
                  {section.subtitle && (
                    <Text
                      fontSize={"13px"}
                      fontWeight={"400"}
                      color={"#666666"}
                      mb={"16px"}
                    >
                      {section.subtitle}
                    </Text>
                  )}
                </Box>
                <Grid
                  display={{ base: "grid", md: "inline-grid" }}
                  w={{ base: "100%", md: "80%" }}
                  templateColumns={{
                    base: "repeat(2, 1fr)",
                    md:
                      section.stats.length < 3
                        ? "repeat(2, 1fr)"
                        : "repeat(3, 1fr)",
                  }}
                  gap={3}
                  mb={{ base: 2, md: 0 }}
                  alignItems="stretch"
                >
                  {section.stats.map((stat) => (
                    <Stat
                      key={stat.title}
                      label={stat.title}
                      value={stat.value}
                      height="71px"
                    />
                  ))}
                </Grid>
              </Stack>
              <Divider />
            </Stack>
          ))}
        </VStack>
      ) : null}
      {!isDraftEvent ? (
        <TicketSalesChart
          data={metrics}
          isLoading={isLoading || isLoadingMetrics}
        />
      ) : null}
    </>
  );
};

export default TicketSalesSection;
